<script setup lang="ts">
import GiveawayBannerImage from '@/assets/images/carousel-banners/giveaway-banner.png'
import ChevronLeftIcon from '@/components/icons/ChevronLeftIcon.vue'
import ChevronRightIcon from '@/components/icons/ChevronRightIcon.vue'
import { computed, markRaw, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { twMerge } from 'tailwind-merge'
import TONBanner from '@/components/TONBanner.vue'
import router from '@/router'
import { RouteName } from '@/routes'
import { useModals } from '@/composables/modal-helper'
import DailyRadarBanner from '@/components/DailyRadarBanner.vue'
import AchievementsBanner from '@/components/AchievementsBanner.vue'
import PathwayEventsBanner from '@/components/PathwayEventsBanner.vue'
import PathwayChallengeBanner from '@/components/PathwayChallengeBanner.vue'
import { usePathwayEventOverlay, usePathwayEvents } from '@/composables/game/pathway-events'
import DailyUpChallengeBanner from '@/components/DailyUpChallengeBanner.vue'
import {
  useDailyUpChallenge,
  useMinesweeperChallenge,
  usePathwayCompletionChallenge,
  usePuzzleChallenge,
  useSpecialCollectibleChallenge,
} from '@/composables/game/challenges'
import type { PathwayEvent } from '@/types/shared/pathway-event'
import { hideTonPayment } from '@/composables/init'
import LuckyRushBanner from '@/components/LuckyRushBanner.vue'
import { useGiveaway } from '@/composables/game/giveaway'
import DealgramBanner from '@/components/DealgramBanner.vue'
import PuzzleBanner from '@/components/PuzzleBanner.vue'
import MemoryChallengeBanner from '@/components/MemoryChallengeBanner.vue'
import { usePuzzles, usePuzzlesOverlay } from '@/composables/game/puzzles'
import PuzzleChallengeBanner from '@/components/PuzzleChallengeBanner.vue'
import { useMemoryChallenge } from '@/composables/game/challenges'
import { useMemoryChallengeModal } from '@/composables/game/memory-challenge-modal'
import ArcadeCenterBanner from '@/components/ArcadeCenterBanner.vue'
import DropeePresaleBanner from '@/components/DropeePresaleBanner.vue'
import MinesweeperChallengeBanner from '@/components/MinesweeperChallengeBanner.vue'
import { useMinesweeperChallengeModal } from '@/composables/game/minesweeper-challenge-modal'
import { useCollectibleChallengeModal } from '@/composables/game/collectible-challenge-modal'
import CollectibleChallengeBanner from '@/components/CollectibleChallengeBanner.vue'

const { setShowTransactionModal, setShowRadarTransactionModal, setShowDailyUpChallengeModal, setShowPathwayChallengeModal, setShowPuzzleChallengeModal, setShowPresaleModal } = useModals()
const { anyActivePathwayEvent, pathwayEvents } = usePathwayEvents()
const { setSelectedPathwayEvent } = usePathwayEventOverlay()
const { dailyUpChallenge } = useDailyUpChallenge()
const { isPathwayCompletionChallengeLive } = usePathwayCompletionChallenge()
const { isPuzzleChallengeLive } = usePuzzleChallenge()
const { countdown } = useGiveaway()
const { openPuzzlesOverlay } = usePuzzlesOverlay()
const { showPuzzle } = usePuzzles()
const { isMemoryChallengeLive } = useMemoryChallenge()
const { openMemoryChallengeModal } = useMemoryChallengeModal()
const { isMinesweeperChallengeLive } = useMinesweeperChallenge()
const { openMinesweeperChallengeModal } = useMinesweeperChallengeModal()
const { isSpecialCollectibleChallengeLive } = useSpecialCollectibleChallenge()
const { openCollectibleChallengeModal } = useCollectibleChallengeModal()

const showDailyUpChallenge = computed(() => dailyUpChallenge.value !== null)
const bannersWrapper = ref<HTMLElement | null>(null)
const bannersContainer = ref<HTMLElement | null>(null)
const activeDailyUpChallenge = computed(() => !!dailyUpChallenge.value)

const dragging = ref(false)
const cursorStartX = ref(0)
const cursorCurrentX = ref(0)
const diffX = computed(() => {
  return cursorStartX.value - cursorCurrentX.value
})

const intervalId = ref<ReturnType<typeof setInterval> | null>(null)

const banners = computed(() => [
  {
    id: 1,
    show: ref(false),
    component: markRaw(DropeePresaleBanner),
    onClick: () => setShowPresaleModal(true),
  },
  {
    id: 2,
    show: ref(true),
    component: markRaw(ArcadeCenterBanner),
    onClick: () => window.open('https://arcade.dropee.xyz', '_blank'),
  },
  {
    id: 3,
    show: computed(() => isSpecialCollectibleChallengeLive.value),
    component: markRaw(CollectibleChallengeBanner),
    onClick: () => openCollectibleChallengeModal(),
  },
  {
    id: 4,
    show: ref(true),
    component: markRaw(DealgramBanner),
    onClick: () => window.open('https://t.me/DealGramAppBot/open', '_blank'),
  },
  {
    id: 5,
    show: computed(() => isPuzzleChallengeLive.value),
    component: markRaw(PuzzleChallengeBanner),
    onClick: () => setShowPuzzleChallengeModal(true),
  },
  {
    id: 6,
    show: ref(isPathwayCompletionChallengeLive.value),
    component: markRaw(PathwayChallengeBanner),
    onClick: () => setShowPathwayChallengeModal(true),
  },
  {
    id: 7,
    show: computed(() => isMemoryChallengeLive.value),
    component: markRaw(MemoryChallengeBanner),
    onClick: () => openMemoryChallengeModal(),
  },
  {
    id: 8,
    show: computed(() => isMinesweeperChallengeLive.value),
    component: markRaw(MinesweeperChallengeBanner),
    onClick: () => openMinesweeperChallengeModal(),
  },
  {
    id: 9,
    show: anyActivePathwayEvent,
    component: markRaw(PathwayEventsBanner),
    onClick: () => {
      setSelectedPathwayEvent(pathwayEvents.value.find(event => !event.hidden) as PathwayEvent)
    },
  },
  {
    id: 10,
    show: ref(!!countdown.value),
    component: markRaw(LuckyRushBanner),
    onClick: () => router.push({ name: RouteName.GIVEAWAY }),
  },
  {
    id: 11,
    show: showDailyUpChallenge,
    component: markRaw(DailyUpChallengeBanner),
    onClick: () => setShowDailyUpChallengeModal(true),
  },
  {
    id: 12,
    show: ref(!hideTonPayment.value && activeDailyUpChallenge.value),
    component: markRaw(DailyRadarBanner),
    onClick: () => setShowRadarTransactionModal(true),
  },
  { id: 13, show: ref(!hideTonPayment.value), component: markRaw(TONBanner), onClick: () => setShowTransactionModal(true) },
  {
    id: 14,
    show: ref(true),
    component: markRaw(AchievementsBanner),
    onClick: () => router.push({ name: RouteName.PROFILE }),
  },
  {
    id: 15,
    show: computed(() => showPuzzle.value),
    component: markRaw(PuzzleBanner),
    onClick: () => openPuzzlesOverlay(),
  },
])

const visibleBanners = computed(() => {
  return banners.value.filter(b => b.show.value === true)
})

const bannersCount = computed(() => visibleBanners.value.length)

const currentBannerIndex = ref(0)

const bannerWidth = computed(() => {
  if (!bannersWrapper.value) return 0
  return bannersWrapper.value.clientWidth
})

function onGoLeft() {
  if (currentBannerIndex.value) {
    currentBannerIndex.value--
  } else {
    currentBannerIndex.value = bannersCount.value - 1
  }
}

function onGoRight() {
  if (currentBannerIndex.value !== (bannersCount.value - 1)) {
    currentBannerIndex.value++
  } else {
    currentBannerIndex.value = 0
  }
}

function shiftBanners(shiftDirection: number) {
  if (!bannersContainer.value) return
  const currentLeft = Math.abs(parseFloat(bannersContainer.value.style.left))
  const newLeft = Math.max(0, Math.min(currentLeft + (shiftDirection * bannerWidth.value), bannersCount.value * bannerWidth.value))
  bannersContainer.value.style.left = `-${newLeft}px`
  startAutoSlide()
}

function startAutoSlide() {
  stopAutoSlide()
  intervalId.value = setInterval(onGoRight, 5000)
}

function stopAutoSlide() {
  if (intervalId.value) {
    clearInterval(intervalId.value)
    intervalId.value = null
  }
}

function getCursorX(event: TouchEvent) {
  if (event.touches && event.touches.length) {
    return event.touches[0].pageX
  }
  return 0
}

function startDrag(e: TouchEvent) {
  dragging.value = true
  cursorStartX.value = getCursorX(e)
  cursorCurrentX.value = cursorStartX.value
}

function drag(e: TouchEvent) {
  e.preventDefault()
  if (!dragging.value) {
    return
  }
  cursorCurrentX.value = getCursorX(e)
}

function stopDrag() {
  dragging.value = false
}

watch(dragging, (value) => {
  if (!value) {
    if (Math.abs(diffX.value) > 20) {
      if (diffX.value > 0) {
        onGoRight()
      } else {
        onGoLeft()
      }
    }
  }
})

watch(() => bannersContainer.value, () => {
  if (!bannersContainer.value) return
  bannersContainer.value.style.left = '0px'
}, { immediate: true })

watch(currentBannerIndex, (value, oldValue) => {
  shiftBanners(value - oldValue)
})

onMounted(() => {
  startAutoSlide()
})

onBeforeUnmount(() => {
  stopAutoSlide()
})

</script>
<template>
  <div class="flex flex-col items-center justify-center relative max-h-[100px] w-auto">
    <div
      ref="bannersWrapper"
      class="w-auto overflow-hidden flex items-center rounded-xl relative scale-up banners-container"
      @touchstart="startDrag"
      @touchmove="drag"
      @touchend="stopDrag"
    >
      <img
        :src="GiveawayBannerImage"
        class="invisible h-full w-auto"
      >
      <div
        ref="bannersContainer"
        class="absolute top-0 w-max h-full flex items-center z-[2] transition-all duration-300"
      >
        <component
          v-for="banner in visibleBanners"
          :key="banner.id"
          :is="banner.component"
          @click="banner.onClick()"
        />
      </div>
    </div>
    <div
      v-if="bannersCount > 1"
      class="flex items-center justify-center gap-x-2 absolute max-[380px]:bottom-0 -bottom-1.5 left-1/2 translate-y-full -translate-x-1/2"
    >
      <ChevronLeftIcon
        class="size-5 text-white"
        @click="onGoLeft"
      />
      <div
        v-for="index in bannersCount"
        :key="index - 1"
        :class="twMerge('rounded-full w-1.5 min-w-1.5 h-1.5 bg-dark-top-layer', currentBannerIndex === index - 1 && 'bg-green-primary')"
        @click="currentBannerIndex = index - 1"
      />

      <ChevronRightIcon
        class="size-5 text-white"
        @click="onGoRight"
      />
    </div>
  </div>
</template>
<style scoped>
.scale-up {
	animation: scale-up 0.75s ease infinite alternate;
}

@keyframes scale-up {
	from {
		transform: scale(1);
	}
	to {
		transform: scale(1.03);
	}
}

.banners-container {
	background: linear-gradient(
		90.15deg,
		rgba(99, 91, 255, 0.1) -8.43%,
		rgba(220, 65, 251, 0.1) 83.79%,
		rgba(255, 112, 150, 0.1) 97.59%
	); /* transparent background color */
	border-radius: 13px;
}

.banners-container::before {
	background: radial-gradient(ellipse at top, rgba(255, 255, 255, 38%), transparent),
	radial-gradient(ellipse at bottom, #2AA683, transparent);
	border-radius: 13px;
	content: "";
	inset: 0;
	-webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
	-webkit-mask-composite: xor;
	mask-composite: exclude;
	padding: 2px; /* this is the border width */
	position: absolute;
	z-index: 10;
	pointer-events: none;
}
</style>
